import { assignSafe, SDate, SDuration } from '@summit/core';
import { BatchChangeType, BatchChangeTypeMapping } from './batch-change-type.enum';
import { BatchDateHandling, BatchDateHandlingMapping } from './batch-date-handling.enum';
import { BatchChangeFunction, DisplaySchema, DisplayType, SummitIdObject } from './models';

export class BatchChange<T> extends SummitIdObject {
  type: BatchChangeType;
  dateHandling: BatchDateHandling;
  ids?: (string | number)[]; //Used for "Existing/Old" Items in ApplyChanges
  source?: Partial<T>;
  changes?: Partial<T>;
  function?: number;
  jsonData?: string;
  bulkData?: Partial<T>[];
  //Local values
  moreComing?: boolean;

  constructor(jsonData?: Partial<BatchChange<T>>) {
    //Note: this does fill in ID but id is not used in the batch change object
    super(jsonData);
    this.type ??= BatchChangeType.Copy;
    this.dateHandling ??= BatchDateHandling.Replace;
  }

  toShortString(): string {
    if (this.type == BatchChangeType.Delete)
      return `${BatchChangeTypeMapping[this.type]}:${BatchDateHandlingMapping[this.dateHandling]} with ${
        this.ids?.length ?? 0
      } IDS`;
    if (this.type == BatchChangeType.Function) {
      return `${BatchChangeTypeMapping[this.type]}:${BatchDateHandlingMapping[this.dateHandling]} with ${
        this.ids?.length ?? 0
      } IDS and calling Function ${this.function}`;
    }
    if (this.type == BatchChangeType.ApplyChanges) {
      return `${BatchChangeTypeMapping[this.type]}:${this.ids?.length ?? 0} Items`;
    }
    return `${BatchChangeTypeMapping[this.type]}:${BatchDateHandlingMapping[this.dateHandling]} with ${
      this.ids?.length ?? 0
    } IDS and fields of ${this.changes ? Object.keys(this.changes)?.join(', ') : '[None]'}`;
  }

  clearEmptyChanges() {
    if (!this.changes) return;
    Object.keys(this.changes).forEach((key) => {
      const value = this.changes?.[key as keyof T];
      if (value == null || value == undefined || value == '') delete this.changes?.[key as keyof T];
    });
  }

  applyTo(data: SummitIdObject[]) {
    const lclChanges = { ...this.changes } as Partial<SummitIdObject>;
    delete lclChanges.id;
    switch (this.type) {
      case BatchChangeType.Function: {
        switch (this.function) {
          case BatchChangeFunction.AddNumber: {
            data.forEach((item) => {
              if (item.id !== null && this.ids?.includes(item.id)) {
                Object.keys(lclChanges).forEach((key) => {
                  const addValue = lclChanges[key as keyof SummitIdObject];
                  const value = item[key as keyof SummitIdObject];
                  item[key as keyof SummitIdObject] = (value as number) + (addValue as number);
                });
              }
            });
            break;
          }
          default:
            console.debug(`Unknown Batch Change Function type ${this.function}`);
            break;
        }
        break;
      }
      case BatchChangeType.Update: {
        //console.log('BatchChangeType.Update', this, data);
        data.forEach((item) => {
          if (item.id !== null && this.ids?.includes(item.id)) {
            assignSafe(item, lclChanges);
          }
        });
        break;
      }
      case BatchChangeType.Delete: {
        this.ids?.forEach((id) => {
          const removeIndex = data.findIndex((entry) => entry.id === id);
          data.splice(removeIndex, 1);
        });
        break;
      }
      default:
        break;
    }
  }

  static Encode<T extends SummitIdObject>(value: string, key: string, schema: DisplaySchema<T>): string | undefined {
    const attribute = schema.getAttribute(key);
    if (!attribute) return value?.toString();
    switch (attribute.displayType) {
      case DisplayType.DateLocal:
        if (!value) return undefined;
        if (SDate.isDayjs(value)) return value.format();
        return SDate.new(value).format();
      case DisplayType.DateUtc:
        if (!value) return undefined;
        if (SDate.isDayjs(value)) return value.format();
        return SDate.newUtc(value).format();
      case DisplayType.Duration: {
        if (!value) return undefined;
        if (SDuration.isDuration(value)) return value.toJSON();
        return SDuration.new(value).toJSON();
      }
      default: {
        //console.log(`${key}: Other`, value);
        return value?.toString();
      }
    }
  }
}
